import StudentService from '../services/student.service';
import UtilService from '../services/util';
import CourseService from '../services/course.service';

const initialState = {
  applications: [],
  copyApplications: [],
  courseMapModerator: [],
  editCell: {
    id: null,
    fieldName: null,
    value: null,
  },
  activeConversation: {
    courseDocuments: [],
    studentId: null,
    id: null,
    studentName: '',
    courseTitle: '',
  },
  notifications: [],
  courses: [],
  offset: 0,
  totalNoApplcations: 0,
  filters: localStorage.getItem('applicationFilters')
    ? JSON.parse(localStorage.getItem('applicationFilters'))
    : null,
};

export const application = {
  namespaced: true,
  state: initialState,
  actions: {
    storeFetchApplications({ commit }) {
      const currentUser = UtilService.currentUser();
      const isManager = UtilService.isManagement() ? null : currentUser.id;
      const filters =
        initialState.filters && initialState.filters.userId === currentUser.id
          ? initialState.filters
          : initialState.filters &&
            initialState.filters.userId !== currentUser.id
          ? UtilService.clearFilters()
          : null;

      return StudentService.getAllApplications({
        id: currentUser.id,
        isManager,
        isTeamLeader: currentUser.isTeamLeader,
        teamId: currentUser.teamId,
        offset: initialState.offset,
        filters,
      }).then(
        (response) => {
          commit('setOffset', response.data.count);
          commit('setApplications', response.data.rows);
          commit('setNotifications');
          return Promise.resolve();
        },
        (error) => {
          commit('messageFailure');
          return Promise.reject(error);
        }
      );
    },

    storeFetchCourses({ commit }) {
      CourseService.getAll().then(
        (response) => {
          commit('setCourses', response);
          return Promise.resolve();
        },
        (error) => {
          commit('messageFailure');
          return Promise.reject(error);
        }
      );
    },

    toggleEditCell({ commit }, data) {
      if (data) {
        commit('toggleEditCell', data);
      } else {
        commit('setEditCellValue', data);
      }
    },

    deleteApplication({ commit }, data) {
      commit('deleteApplication', data);
    },

    pushNewApplication({ commit }, data) {
      commit('pushNewApplication', data);
    },

    editApplication({ commit }, data) {
      commit('editApplication', data);
    },

    updateStudentMessages({ commit }, data) {
      commit('updateStudentMessages', data);
    },

    setActiveConversation({ commit }, data) {
      commit('setActiveConversation', data);
    },

    setActiveIntake({ commit }, data) {
      commit('setActiveIntake', data);
      return Promise.resolve();
    },
  },

  mutations: {
    setApplications(state, response) {
      const responseApps = response
        .map((application) => {
          return application.student
            ? {
                id: application.id,
                moderatorId: application.user.id,
                moderator: application.user.username,
                moderatorDeleted: application.user.deleted,
                status: application.status,
                date: application.createdAt,
                updatedAt: application.updatedAt,
                createdAt: application.createdAt,
                tableDate: {
                  createdAt: UtilService.formatDateWithTime(
                    application.student.createdAt
                  ),
                  submittedAt: UtilService.formatDateWithTime(
                    application.student.submittedAt
                  ),
                  approvedAt: UtilService.formatDateWithTime(
                    application.student.approvedAt
                  ),
                },
                studentData: {
                  personalInformation: {
                    student_name: UtilService.studentName(application.student),
                    student_referal: application.student.student_referal,
                    email: application.student.email,
                    phone_number: application.student.phone_number,
                    delivery_mode: application.student.delivery_mode,
                    intake: application.student.intake
                      ? application.student.intake
                      : application.student.student_intake,
                  },
                  generalInfo: {
                    booking: application.student.booking,
                    english_test: application.student.english_test,
                    interview: application.student.interview,
                    studentId: application.student.studentId,
                    student_finance: application.student.student_finance_info,
                  },
                  applicationInformation: {
                    setttlement_status: application.student.setttlement_status,
                  },
                },
                studentId: application.student.id,
                courseData: {
                  timetable_options: application.course.timetable,
                  documents: application.course.documents,
                  deadline: application.course.deadline,
                },
                courseId: application.course.id,
                courseTitle: application.course.title,
                application_messages: application.application_messages,
                application_messages_invites:
                  application.application_messages_invites,
                studentDocuments: application.student.student_attachments,
                studentForm: application.student.student_finance,
                university: {
                  title: application.course.university
                    ? application.course.university.title
                    : '',
                  address: application.course.university
                    ? application.course.university.address
                    : '',
                  id: application.course.university
                    ? application.course.university.id
                    : '',
                },
                unresolvedComments: application.unresolvedComments,
                team: UtilService.getUserTeam(application.user.team_users),
                isUrgent: application.student.isUrgent,
                fullCourse: application.course,
                intake: application.student.intake,
                isApproved: application.student.isApproved,
                isDraft: application.student.isDraft,
                studentApproved: application.student.studentApproved,
                isAdminApproved: application.student.isAdminApproved,
              }
            : null;
        })
        .filter((x) => x);
      state.applications = [...state.applications, responseApps].flat(1);
      state.copyApplications = state.applications;
    },

    setNotifications(state) {
      const tempNotif = state.applications.map((app) => {
        return {
          studentName: app.studentData.personalInformation.student_name,
          studentId: app.studentId,
          status: app.status,
          studentFormStatus: app.studentForm
            ? app.studentForm.studentFormStatus
            : null,
          studentFormLastUpdate: app.studentForm
            ? app.studentForm.updatedAt
            : null,
          uploadedPrintScreen: app.studentForm
            ? app.studentForm.printscreenUrl
            : null,
          startedStudentForm: app.studentForm ? true : false,
        };
      });

      state.notifications = tempNotif
        .map((sentApp) => {
          return {
            ...sentApp,
            notificationStatus: UtilService.getNotificationStatus(sentApp),
          };
        })
        .filter((x) => x.notificationStatus);
    },

    updateStudentMessages(state, data) {
      const { studentId, messages } = data;
      const openedStudentIndex = state.applications.findIndex(
        (app) => app.studentId === studentId
      );
      if (openedStudentIndex > -1) {
        state.applications[openedStudentIndex].application_messages = messages;
      }
    },

    setOffset(state, count) {
      state.offset += 100;
      state.totalNoApplcations = count;
    },

    toggleEditCell(state, data) {
      const { studentValue, path, value, setData } = data;
      const properties = Array.isArray(path) ? path : path.split('.');
      const fieldName = properties[properties.length - 1];
      const valueToQuery = setData
        ? UtilService.deepCopy(
            state.applications.filter((app) => app.id === studentValue)[0]
          )
        : studentValue;
      properties.reduce((prev, curr) => {
        if (curr === fieldName && prev) {
          prev[curr] = value;
        }

        if (prev) {
          return prev[curr];
        }
      }, valueToQuery);

      const indexId = setData
        ? state.applications.map((app) => app.id).indexOf(studentValue)
        : -1;

      if (indexId > -1) {
        state.applications.splice(indexId, 1, valueToQuery);
      }

      state.editCell = {
        id: studentValue.id,
        studentId: studentValue.studentId,
        fieldName,
        value,
        path,
      };
    },

    setEditCellValue(state, data) {
      if (!data) {
        state.editCell = {
          id: null,
          fieldName: null,
          value: null,
        };
      }
    },

    setCourses(state, response) {
      state.courses = response.data;
      state.courseMapModerator = response.data.map((course) => {
        return {
          ...course,
          moderatorId:
            course.university && course.university.university_admins
              ? course.university.university_admins.map((admin) => admin.userId)
              : null,
          uniTitle:
            course.university && course.university.title
              ? course.university.title
              : null,
        };
      });
    },

    setActiveConversation(state, data) {
      state.activeConversation = data
        ? data
        : {
            courseDocuments: [],
            studentId: null,
            moderatorId: null,
            id: null,
            studentName: '',
            courseTitle: '',
          };
    },

    setActiveIntake(state, data) {
      state.activeIntake = data;
      localStorage.setItem('activeIntake', data);
    },

    messageFailure() {},

    editApplication(state, data) {
      const indexId = state.applications.map((app) => app.id).indexOf(data.id);

      if (indexId > -1) {
        let copiedApp = {
          ...[...state.applications][indexId],
        };

        copiedApp[data.path] = data.value;

        state.applications.splice(indexId, 1, copiedApp);
      }
    },

    pushNewApplication(state, newApplication) {
      state.applications = [...state.applications].concat(newApplication);
    },

    deleteApplication(state, id) {
      const indexId = state.applications.map((app) => app.id).indexOf(id);
      state.applications.splice(indexId, 1);
    },
  },
};
